import { FIRSTPARTY_ENDPOINT, FIRSTPARTY_UPLOAD_ENDPOINT } from '@/constants/endpoints';
import { DEFAULT_USER } from '@/constants/userManagement/userForm';
import { getAdvertisersFromClients, getUniqueAdvertisers } from '@/helpers/advertisers';
import { fetchClients } from '@/helpers/axios';
import { formatClientsResponseData } from '@/helpers/clients';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  clients: [],
  advertiserList: [],
  clientsError: null,
  uploadFirstPartyError: null,
  uploadFirstPartyRequestId: null,
  filters: {
    clients: [],
    advertisers: [],
    page: 1,
    limit: 15,
    sort: null,
  },
  firstPartyData: [],
  firstPartyDataRequestId: null,
  clientsRequestId: null,
};

export const firstPartyDataSlice = createSlice({
  name: 'firstPartyData',
  initialState,
  reducers: {
    resetSelectedUser: state => {
      state.selectedUserId = null;
      state.selectedUser = DEFAULT_USER;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSelectedUserId: (state, action) => {
      state.selectedUserId = action.payload.selectedUserId;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getClients.pending, pendingCaseHandler('clients'))
      .addCase(getClients.rejected, rejectedCaseHandler('clients'))
      .addCase(getClients.fulfilled, (state, action) => {
        if (state.clientsRequestId === action.meta.requestId) {
          state.clientsRequestId = null;
          state.clients = action.payload;
          const advertisers = getAdvertisersFromClients(action.payload);
          state.advertiserList = getUniqueAdvertisers(advertisers);
        }
      })
      .addCase(uploadFirstPartyData.pending, pendingCaseHandler('uploadFirstParty'))
      .addCase(uploadFirstPartyData.rejected, rejectedCaseHandler('uploadFirstParty'))
      .addCase(uploadFirstPartyData.fulfilled, fulfilledCaseHandler('uploadFirstParty'))
      .addCase(getFirstPartyData.pending, pendingCaseHandler('firstPartyData'))
      .addCase(getFirstPartyData.rejected, rejectedCaseHandler('firstPartyData'))
      .addCase(getFirstPartyData.fulfilled, fulfilledCaseHandler('firstPartyData'))
      .addCase(signUpDataAgreement.pending, pendingCaseHandler('dataAgreement'))
      .addCase(signUpDataAgreement.rejected, rejectedCaseHandler('dataAgreement'))
      .addCase(signUpDataAgreement.fulfilled, fulfilledCaseHandler('dataAgreement'));
  },
});

export const { resetSelectedUser, setFilters, setSelectedUserId } = firstPartyDataSlice.actions;

export const uploadFirstPartyData = createAsyncThunk(
  'firstPartyDataSlice/uploadFirstPartyData',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.postForm(FIRSTPARTY_UPLOAD_ENDPOINT, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getFirstPartyData = createAsyncThunk(
  'firstPartyDataSlice/getFirstPartyData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(FIRSTPARTY_ENDPOINT);

      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const getClients = createAsyncThunk('firstPartyDataSlice/getClients', async (payload, { rejectWithValue }) => {
  try {
    const response = await fetchClients(null, payload?.signal);
    return formatClientsResponseData(response);
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const signUpDataAgreement = createAsyncThunk(
  'firstPartyDataSlice/signUpDataAgreement',
  async (_payload, { rejectWithValue }) => {
    try {
      return await axios.put(`/users/signAgreement`);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export default firstPartyDataSlice.reducer;
